import { matchPath } from "react-router";

import { REFERENCIAS_DE_MERCADO } from "./modulos";

import {
  leerOrigen,
  ORIGEN_DE_LA_EDICION,
  raizDelOrigen,
  type Origen,
} from "./origenDeNavegacion";

/* ---------------------------------------------------------------------------
   Metadatos de página (título + breadcrumbs) por ruta. Fuente única que lee
   el TopBar según la URL. Convención: el título de cada página vive en el
   header, no dentro del contenido.

   LA MIGA RAÍZ VOLVIÓ, y es "Inicio" apuntando a `/`. La había, se retiró
   junto con la portada de accesos que la recibía, y el diseño la trae de
   vuelta ("Inicio / Enlazar referencias de mercado").

   La objeción de entonces sigue en pie y esto NO la contradice: lo que se
   descartó fue colgar todo de "Informes en proceso", que afirmaría que las
   revisiones y la administración son hijas de la cola del ejecutivo. "Inicio"
   no afirma eso — no es el PADRE de nada, es la puerta de la aplicación, que
   es lo que una miga raíz significa en cualquier sistema. Y `/` existe: es
   la entrada real, que redirige a `/informes`.

   La raíz NO se escribe en cada entrada: la antepone el `TopBar`. Así una
   pantalla nueva la hereda sin que nadie se acuerde.

   `crumbs: []` sigue siendo válido y ahora significa "mi miga es mi título":
   el `TopBar` lo usa de respaldo. Solo se declara `crumbs` cuando el nombre
   de la SECCIÓN difiere del título de la pantalla, que es el caso del paso 1
   de referencias.
--------------------------------------------------------------------------- */

export interface Crumb {
  label: string;
  /** Si tiene url, el crumb es un enlace; si no, es texto (crumb intermedio
      no navegable o la página actual). */
  url?: string;
}

export interface PageMeta {
  title: string;
  crumbs: Crumb[];
}

/**
 * El segundo argumento es la QUERY STRING, no un extra decorativo: hay
 * pantallas a las que se llega desde más de un lugar y cuyo camino de miga
 * depende de cuál fue (ver `origenDeNavegacion.ts`). Sin él, el breadcrumb
 * afirmaría siempre el mismo recorrido y dos de cada tres visitas verían un
 * camino por el que no pasaron.
 */
type Resolver =
  | PageMeta
  | ((params: Record<string, string | undefined>, search: URLSearchParams) => PageMeta);

/* Orden: coincidencia exacta (end:true), así que el orden no es crítico, pero
   se listan de lo más específico a lo más general por claridad. */
/* Perfiles es un submódulo del Mantenedor de usuarios: los crumbs muestran esa
   jerarquía aunque la ruta sea hermana (/admin/perfiles, no /admin/usuarios/…),
   porque un perfil es una entidad propia y no un recurso de un usuario. */
/* El nombre de la SECCIÓN de referencias de mercado, que no es el título de
   ninguna de sus tres pantallas: aquellas dicen qué hacer ahí ("Seleccione el
   informe a trabajar"), y la miga dice dónde estás. Sale del diseño. */
const REFERENCIAS_SECCION = "Enlazar referencias de mercado";

const ADMIN_USUARIOS: Crumb[] = [
  { label: "Administración" },
  { label: "Usuarios", url: "/admin/usuarios" },
];
const ADMIN_PERFILES: Crumb[] = [...ADMIN_USUARIOS, { label: "Perfiles", url: "/admin/perfiles" }];

/* El Mantenedor de IA pasó a agrupar dos pantallas (odd/tasks/consumo-de-ia.md):
   Funciones (`/admin/ia`, sin cambios) y Consumo y costos. El crumb del medio
   navega de vuelta a la primera — misma forma que ADMIN_USUARIOS con
   "Usuarios" — porque esa sigue siendo la pantalla por defecto del grupo. */
const ADMIN_IA: Crumb[] = [
  { label: "Administración" },
  { label: "Mantenedor de IA", url: "/admin/ia" },
];

/* Las tres pantallas del Mantenedor de Informes (contenidos, contextos y
   revisión) cuelgan de un rótulo que NO navega — es un grupo del menú, no una
   de sus pantallas (`menu.ts`, regla de `webapp/CLAUDE.md`). Por eso el crumb
   del medio va SIN `url`: darle una lo convertiría en enlace a una pantalla
   que no existe. */
/* Ruta de trabajo (no administración): el listado de informes en proceso es el
   padre de crear y de editar. */
const INFORMES: Crumb[] = [{ label: "Informes en proceso", url: "/informes" }];

/* La otra cola de trabajo. Hermana de INFORMES, no hija: el revisor no llega a
   su bandeja pasando por el listado del ejecutivo. */
const REVISIONES: Crumb[] = [{ label: "Revisiones pendientes", url: "/revisiones" }];

const ADMIN_INFORMES: Crumb[] = [
  { label: "Administración" },
  { label: "Mantenedor de Informes" },
];

const ADMIN_CONTENIDOS: Crumb[] = [...ADMIN_INFORMES, { label: "Contenidos", url: "/admin/contenidos" }];

/**
 * El camino hasta la pantalla de la que se vino, para las pantallas que se
 * abren desde más de un lugar.
 *
 * Es el MISMO recorrido que hace el "Volver" (`retornoA`), y a propósito: el
 * último crumb de este camino es exactamente el destino de ese enlace. Dos
 * tablas separadas se contradirían en cuanto una cambiara.
 *
 * Cada camino arranca en la raíz de su sección —la cola del ejecutivo o la
 * bandeja del revisor— porque son las dos únicas pantallas que existen siempre.
 */
function caminoDelOrigen(origen: Origen, id: string | undefined): Crumb[] {
  const raiz = raizDelOrigen(origen) === "revisiones" ? REVISIONES : INFORMES;

  switch (origen) {
    // La raíz ES el origen: no hay nada que agregarle.
    case "informes":
    case "revisiones":
      return raiz;
    case "informe":
      return [...raiz, { label: "Informe", url: `/informes/${id}` }];
    case "cotizacion":
      return [...raiz, { label: "Cotización", url: `/informes/${id}/cotizacion/enviada` }];
    case "visado":
      return [...raiz, { label: "Visado", url: `/revisiones/${id}` }];
  }
}

/**
 * El header de la EDICIÓN del informe, que sirve a DOS patrones.
 *
 * La edición se abre desde CUATRO pantallas, así que su camino de miga sale
 * del origen declarado en la url —igual que su enlace de volver, ver
 * `origenDeNavegacion.ts`—. Con un camino fijo, quien entraba desde la bandeja
 * volvía por el listado del ejecutivo, donde nunca estuvo.
 *
 * SE EXTRAJO PARA QUE `/informes/:id` Y `/informes/:id/:pestana` NO PUEDAN
 * DIVERGIR. La pestaña no es una pantalla nueva: es la misma edición,
 * direccionada con más precisión. Declarada aparte, la miga cambiaría al
 * cambiar de pestaña y afirmaría un recorrido distinto por estar mirando otra
 * parte de la misma pantalla.
 *
 * El nombre de la pestaña NO entra en el título ni en la miga: ya está en la
 * tarjeta activa de la barra, y repetirlo arriba sería el mismo dato dos veces
 * a treinta píxeles de distancia.
 */
const metaDeLaEdicion: Resolver = (params, search) => {
  const origen = leerOrigen(search, ORIGEN_DE_LA_EDICION);

  return {
    title: "Editar informe",
    // La edición no puede venir de SÍ MISMA: `informe` como origen daría un
    // crumb que apunta a la página que se está mirando.
    crumbs: [
      ...caminoDelOrigen(origen === "informe" ? "informes" : origen, params.id),
      { label: "Editar informe" },
    ],
  };
};

/**
 * Metadatos de una URL que no corresponde a ninguna pantalla.
 *
 * Existe porque `getPageMeta` devuelve `null` para una URL que no es ninguna
 * pantalla, y el TopBar necesita igual un título que poner arriba del cuerpo
 * que dice que la página no existe.
 */
export const NOT_FOUND_META: PageMeta = {
  title: "Página no encontrada",
  crumbs: [],
};

const routes: { pattern: string; meta: Resolver }[] = [
  {
    pattern: "/admin/usuarios",
    meta: { title: "Usuarios", crumbs: [{ label: "Administración" }, { label: "Usuarios" }] },
  },
  {
    pattern: "/admin/usuarios/nuevo",
    meta: {
      title: "Crear usuario",
      crumbs: [...ADMIN_USUARIOS, { label: "Crear usuario" }],
    },
  },
  {
    pattern: "/admin/usuarios/:id/editar",
    meta: {
      title: "Editar usuario",
      crumbs: [...ADMIN_USUARIOS, { label: "Editar usuario" }],
    },
  },
  {
    pattern: "/admin/contenidos",
    meta: {
      title: "Plantillas de informe",
      crumbs: [...ADMIN_INFORMES, { label: "Contenidos" }],
    },
  },
  /* El maestro de ítems y el editor de una plantilla cuelgan del listado. Los
     patrones no se pisan entre sí (uno es estático y el otro lleva `plantillas/`
     delante del id), pero el crumb del padre SÍ navega: es de donde se entró. */
  {
    pattern: "/admin/contenidos/maestro",
    meta: {
      title: "Maestro de ítems",
      crumbs: [...ADMIN_CONTENIDOS, { label: "Maestro de ítems" }],
    },
  },
  {
    pattern: "/admin/contenidos/formatos",
    meta: {
      title: "Formatos de salida",
      crumbs: [...ADMIN_CONTENIDOS, { label: "Formatos de salida" }],
    },
  },
  {
    pattern: "/admin/contenidos/formatos/:id",
    meta: {
      title: "Formato de salida",
      crumbs: [
        ...ADMIN_CONTENIDOS,
        { label: "Formatos de salida", url: "/admin/contenidos/formatos" },
        { label: "Formato" },
      ],
    },
  },
  {
    pattern: "/admin/contenidos/plantillas/:id",
    meta: {
      title: "Editar plantilla",
      crumbs: [...ADMIN_CONTENIDOS, { label: "Plantilla" }],
    },
  },
  {
    pattern: "/admin/contextos",
    meta: {
      title: "Contextos",
      crumbs: [...ADMIN_INFORMES, { label: "Contextos" }],
    },
  },
  {
    pattern: "/admin/revision-informes",
    meta: {
      title: "Revisión de informes",
      crumbs: [...ADMIN_INFORMES, { label: "Revisión de informes" }],
    },
  },
  {
    pattern: "/admin/perfiles",
    meta: {
      title: "Perfiles de usuario",
      crumbs: [...ADMIN_USUARIOS, { label: "Perfiles" }],
    },
  },
  {
    pattern: "/admin/perfiles/nuevo",
    meta: {
      title: "Crear perfil de usuario",
      crumbs: [...ADMIN_PERFILES, { label: "Crear perfil" }],
    },
  },
  {
    pattern: "/admin/perfiles/:id/editar",
    meta: { title: "Editar perfil", crumbs: [...ADMIN_PERFILES, { label: "Editar perfil" }] },
  },
  {
    pattern: "/admin/ia",
    meta: {
      title: "Mantenedor de IA",
      crumbs: [{ label: "Administración" }, { label: "IA" }],
    },
  },
  {
    pattern: "/admin/ia/laboratorio",
    meta: {
      title: "Laboratorio de IA",
      crumbs: [...ADMIN_IA, { label: "Laboratorio de IA" }],
    },
  },
  {
    pattern: "/admin/ia/consumo",
    meta: {
      title: "Consumo y costos de IA",
      crumbs: [...ADMIN_IA, { label: "Consumo y costos" }],
    },
  },
  {
    pattern: "/admin/cotizacion",
    meta: {
      title: "Mantenedor de cotización",
      crumbs: [{ label: "Administración" }, { label: "Cotización" }],
    },
  },
  {
    pattern: "/admin/homologacion",
    meta: {
      title: "Tablas de homologación",
      crumbs: [{ label: "Administración" }, { label: "Homologación" }],
    },
  },
  {
    pattern: "/admin/clases-de-suelo",
    meta: {
      title: "Clases de uso de suelo",
      crumbs: [{ label: "Administración" }, { label: "Clases de uso de suelo" }],
    },
  },
  {
    pattern: "/admin/empresa",
    meta: {
      title: "Datos de la empresa",
      crumbs: [{ label: "Administración" }, { label: "Datos de la empresa" }],
    },
  },
  {
    pattern: "/admin/configuracion",
    meta: {
      title: "Configuración",
      crumbs: [{ label: "Administración" }, { label: "Configuración" }],
    },
  },
  {
    pattern: "/design-system",
    meta: { title: "Design System", crumbs: [] },
  },
  /* Módulo E. NO cuelga de "Administración": trabajar informes es el día a día
     y el spec §8 lo declara visible para los tres perfiles. El crumb del medio
     SÍ navega, porque el listado es una pantalla real. */
  {
    pattern: "/informes",
    meta: { title: "Informes en proceso", crumbs: [] },
  },
  /* Pantalla de PRIMER NIVEL: se llega por el botón de acceso rápido del menú,
     no bajando desde el listado de informes. Colgarla de "Informes en proceso"
     afirmaría que es una vista suya, y el informe todavía no está elegido
     cuando la pantalla se abre.

     ES LA ÚNICA QUE DECLARA SU MIGA SIN COLGAR DE NADIE, y el motivo es que su
     título no sirve de miga: "Seleccione el informe a trabajar" es una
     instrucción, no el nombre de un lugar. La miga nombra la SECCIÓN, como en
     el diseño. */
  {
    pattern: "/referencias",
    meta: {
      title: "Seleccione el informe a trabajar",
      crumbs: [{ label: REFERENCIAS_SECCION }],
    },
  },
  /* Los dos pasos siguientes SÍ cuelgan del paso 1, al revés que aquel: se
     llega desde él y "Cambiar de informe" es una salida real. */
  {
    pattern: "/referencias/:informeId/enlazadas",
    meta: {
      title: "Referencias del informe",
      crumbs: [
        { label: REFERENCIAS_SECCION, url: "/referencias" },
        { label: "Referencias del informe" },
      ],
    },
  },
  {
    pattern: "/referencias/:informeId",
    meta: {
      title: "Comparación con mercado",
      crumbs: [
        { label: REFERENCIAS_SECCION, url: "/referencias" },
        { label: "Comparación con mercado" },
      ],
    },
  },
  {
    pattern: "/informes/nuevo",
    meta: { title: "Crear informe", crumbs: [...INFORMES, { label: "Crear informe" }] },
  },
  /* La edición. Su header lo arma `metaDeLaEdicion`, compartido con el patrón
     de pestaña que va más abajo — el porqué está en esa función. */
  {
    pattern: "/informes/:id",
    meta: metaDeLaEdicion,
  },
  {
    pattern: "/informes/:id/cotizacion",
    meta: (params) => ({
      title: "Cotización del informe",
      crumbs: [
        ...INFORMES,
        // El crumb del informe SÍ navega: desde la cotización se vuelve a la
        // ficha, que es de donde se entró.
        { label: "Informe", url: `/informes/${params.id}` },
        { label: "Cotización" },
      ],
    }),
  },
  {
    pattern: "/informes/:id/cotizacion/enviada",
    meta: (params) => ({
      title: "Cotización del informe",
      crumbs: [
        ...INFORMES,
        { label: "Informe", url: `/informes/${params.id}` },
        { label: "Cotización" },
      ],
    }),
  },
  /* La generación del informe con IA. Hermana ESTÁTICA de la cotización, no
     una pestaña: va ANTES del patrón de pestaña por lo mismo que aquellas —
     `getPageMeta` corta en el primer `matchPath`, así que `/:pestana` se la
     comería y el header diría "Editar informe". El crumb del informe lleva su
     nombre genérico porque este mapa solo conoce la url, no los datos. */
  {
    pattern: "/informes/:id/generacion",
    meta: (params) => ({
      title: "Generación del informe",
      crumbs: [
        ...INFORMES,
        { label: "Informe", url: `/informes/${params.id}` },
        { label: "Generación del informe" },
      ],
    }),
  },
  /* La PESTAÑA del editor. Para el header no es una pantalla nueva: es la
     MISMA edición, direccionada con más precisión, así que reusa su meta en vez
     de declarar un título y un camino que tendrían que mantenerse iguales a
     mano. Si divergieran, la miga cambiaría al cambiar de pestaña y afirmaría
     un recorrido distinto por estar mirando otra parte de la misma pantalla.

     VA DESPUÉS DE LAS DOS DE COTIZACIÓN, y acá el orden SÍ decide: `getPageMeta`
     recorre esta lista y corta en el primer `matchPath`, sin rankear lo
     estático por encima de lo dinámico como hace React Router. Declarado antes,
     este patrón se comería `/informes/7/cotizacion` y el header de la
     cotización diría "Editar informe" sin que ninguna ruta se rompiera.

     Y RESUELVE TAMBIÉN UN SEGMENTO INVENTADO, a propósito: la pantalla redirige
     ante uno, pero el header se arma ANTES de esa redirección, y sin esta
     entrada quedaría el título del 404 encabezando una página que sí existe. */
  {
    pattern: "/informes/:id/:pestana",
    meta: metaDeLaEdicion,
  },
  /* Módulo G. Hermana de "/informes", no hija: es otra cola de trabajo, con
     otro criterio de orden y otro dueño. Por eso NO cuelga del listado de
     informes: es una pantalla de primer nivel, igual que aquella. */
  {
    pattern: "/revisiones",
    meta: { title: "Revisiones pendientes", crumbs: [] },
  },
  {
    pattern: "/revisiones/:id",
    meta: {
      title: "Visado de cotización",
      // El crumb de la bandeja SÍ navega: es de donde se entró y a donde se
      // vuelve al terminar de visar.
      crumbs: [{ label: "Revisiones pendientes", url: "/revisiones" }, { label: "Visado" }],
    },
  },
  {
    pattern: "/novedades",
    meta: { title: "Novedades", crumbs: [] },
  },
  {
    pattern: "/feedback",
    meta: { title: "Feedback", crumbs: [] },
  },
  {
    pattern: "/dashboard-legacy",
    meta: {
      title: "Dashboard (versión anterior)",
      crumbs: [],
    },
  },
];

/**
 * `search` es opcional porque casi ninguna pantalla lo necesita: solo las que
 * se abren desde más de un lugar. Omitirlo da el camino por defecto, que es
 * exactamente lo que corresponde para una url pegada a mano.
 */
export function getPageMeta(pathname: string, search = ""): PageMeta | null {
  for (const r of routes) {
    // Un módulo APAGADO no tiene título, porque no tiene pantalla: su ruta cae
    // en el 404 (ver `lib/modulos.ts` y `App.tsx`). Sin este filtro el 404 de
    // `/referencias` saldría encabezado "Seleccione el informe a trabajar" — un
    // título sobre una página que dice que no existe.
    if (!REFERENCIAS_DE_MERCADO && r.pattern.startsWith("/referencias")) {
      continue;
    }

    const match = matchPath({ path: r.pattern, end: true }, pathname);
    if (match) {
      return typeof r.meta === "function"
        ? r.meta(match.params, new URLSearchParams(search))
        : r.meta;
    }
  }
  return null;
}
